import { buildSetupAuditDiffRows, narrowSetupSummaryDiff } from "./scheduleAuditEvents";

const SCHEDULE_AUDIT_FIELDS = new Set([
  "ScheduleSetup",
  "ScheduleGroups",
  "ScheduleVisits",
  "ScheduleStatus",
]);

const FIELD_DISPLAY = {
  ScheduleSetup: "Schedule setup",
  ScheduleGroups: "Schedule groups",
  ScheduleVisits: "Schedule Visit",
  ScheduleStatus: "Schedule status",
};

const LEGACY_SETUP_ACTIONS = {
  "schedule visits applied": "Schedule setup updated",
  "multi-group schedule setup saved": "Schedule setup saved",
};

const ACTION_LABEL_RE =
  /^(Schedule cloned|Schedule setup(?: saved| updated)?|Schedule published(?: and live| \(live update\))?|Multi-group |Visit (added|removed|renamed|reordered|deleted|details saved|time point(?: saved| updated)?)|Screening details saved|Planner setup(?: saved| updated)?|Visit payment|Visit cost|Template (?:saved|updated) for appointment |Schedule document(?: (?:uploaded|deleted|updated|version uploaded|saved|removed))?|E-communication template(?: (?:saved|updated))?|Group (added|updated|renamed|deleted)|E-communication|Payment saved|Cost saved|Worksheet saved)/i;

function isScheduleAuditRow(row) {
  const source = String(row?.SourceLevel || "").trim();
  const field = String(row?.FieldName || "").trim();
  return source === "Schedule" || SCHEDULE_AUDIT_FIELDS.has(field);
}

export function looksLikeActionLabel(text) {
  const value = String(text || "").trim();
  if (!value) return false;
  return ACTION_LABEL_RE.test(value);
}

function looksLikeStructuredSuffix(text) {
  return /^(visit|group|template|document|payment|cost|reason|published|reference|start|finish|duration|old|new|field)\s*:/i.test(
    String(text || "").trim(),
  );
}

/** Split "Schedule setup updated · Test" into the event name and a free-text reason. */
export function peelReasonFromAction(actionText) {
  const value = String(actionText || "").trim();
  const sep = " · ";
  const sepIndex = value.indexOf(sep);
  if (sepIndex < 0) return { title: value, reason: "" };

  const head = value.slice(0, sepIndex).trim();
  const rest = value.slice(sepIndex + sep.length).trim();
  if (!head || !rest) return { title: value, reason: "" };
  if (!looksLikeActionLabel(head)) return { title: value, reason: "" };
  if (looksLikeStructuredSuffix(rest) || looksLikeActionLabel(rest)) {
    return { title: value, reason: "" };
  }

  return { title: toTitleCaseAction(head), reason: rest };
}

function resolveFreeTextReason({ rawAction, rawComments, rawNew, reasonFromAction }) {
  if (reasonFromAction) return reasonFromAction;

  const comments = String(rawComments || "").trim();
  if (!comments || comments === rawNew) return "";
  if (comments === rawAction) {
    return peelReasonFromAction(comments).reason;
  }

  const peeled = peelReasonFromAction(comments);
  if (peeled.reason) return peeled.reason;
  if (looksLikeActionLabel(comments)) return "";
  return comments;
}

function isSetupAuditAction(rawAction, fieldName) {
  const action = String(rawAction || "").trim().toLowerCase();
  if (fieldName === "ScheduleSetup") return true;
  return Object.prototype.hasOwnProperty.call(LEGACY_SETUP_ACTIONS, action);
}

function toTitleCaseAction(text) {
  const value = String(text || "").trim();
  if (!value) return "Update";
  return value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function normalizeActionTitle(rawAction) {
  const action = String(rawAction || "").trim();
  const legacy = LEGACY_SETUP_ACTIONS[action.toLowerCase()];
  if (legacy) return { title: toTitleCaseAction(legacy), suffix: "" };

  const colonIndex = action.indexOf(":");
  if (colonIndex < 0) {
    return { title: toTitleCaseAction(action), suffix: "" };
  }

  const head = action.slice(0, colonIndex).trim();
  const suffix = action.slice(colonIndex + 1).trim();
  return {
    title: toTitleCaseAction(head),
    suffix,
  };
}

function resolveFieldDisplay(fieldName, rawAction) {
  if (isSetupAuditAction(rawAction, fieldName)) {
    return "Schedule setup";
  }
  return (
    FIELD_DISPLAY[fieldName] ||
    fieldName ||
    (looksLikeActionLabel(rawAction) ? "Schedule" : "—")
  );
}

function formatDetailFromSuffix(suffix, fieldName) {
  const value = String(suffix || "").trim();
  if (!value) return "";

  if (/^(visit|group|template|document|payment|cost|reason|published)/i.test(value)) {
    return value;
  }

  if (fieldName === "ScheduleVisits") {
    return `Visit: ${value}`;
  }
  if (fieldName === "ScheduleGroups") {
    return `Group: ${value}`;
  }

  return value;
}

function visitLabelLine(text) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (!/^Visit:\s*/i.test(value)) return value;
  return value.split("·")[0].trim();
}

function resolveSetupDetail(rawAction, rawComments, rawNew) {
  if (rawComments && rawComments !== rawAction) return rawComments;
  if (rawNew && rawNew !== rawAction) return rawNew;
  return "";
}

function resolveVisitDetailLine({
  rawAction,
  rawComments,
  rawNew,
  rawOld,
  fieldName,
  suffix,
}) {
  if (rawComments && rawComments !== rawAction) {
    return visitLabelLine(rawComments);
  }

  const suffixDetail = formatDetailFromSuffix(suffix, fieldName);
  if (suffixDetail) return suffixDetail;

  if (rawNew && rawNew !== rawAction && rawNew !== rawOld) {
    return visitLabelLine(rawNew);
  }

  return "";
}

function looksLikeFinancialAuditValue(text) {
  return /^(Payment|Cost|Randomisation cost):\s*/i.test(String(text || "").trim());
}

function isVisitValueDiff(rawOld, rawNew, rawAction, detailDisplay) {
  if (String(rawOld || "").trim()) return true;
  if (!String(rawNew || "").trim()) return false;
  if (rawNew === rawAction) return false;
  if (detailDisplay && rawNew === detailDisplay) return false;
  if (looksLikeFinancialAuditValue(rawNew)) return true;
  return looksLikeActionLabel(rawNew);
}

function isEcommTemplateAuditAction(rawAction) {
  return /^Template (?:saved|updated) for appointment /i.test(String(rawAction || "").trim());
}

const ECOMM_AUDIT_FIELD_ORDER = ["Email", "SMS", "Email subject", "Email body", "SMS text"];

function parseEcommAuditSegments(text = "") {
  const fields = new Map();

  String(text || "")
    .split("·")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const colonIndex = part.indexOf(":");
      if (colonIndex < 0) return;
      const key = part.slice(0, colonIndex).trim();
      const value = part.slice(colonIndex + 1).trim();
      if (key === "Visit") {
        fields.set("__visit", `Visit: ${value}`);
        return;
      }
      if (key === "Template") {
        fields.set("__template", `Template: ${value}`);
        return;
      }
      fields.set(key, value);
    });

  return {
    visitLine: fields.get("__visit") || "",
    templateLine: fields.get("__template") || "",
    fields,
  };
}

function ecommAuditTone(label) {
  if (label === "Email subject") return "subject";
  if (label === "Email body") return "email";
  if (label === "SMS text") return "sms";
  return "";
}

export function buildEcommAuditDiffBlocks(oldText = "", newText = "") {
  const oldParsed = parseEcommAuditSegments(oldText);
  const newParsed = parseEcommAuditSegments(newText);
  const hasOld = Boolean(String(oldText || "").trim());
  const keys = new Set([
    ...ECOMM_AUDIT_FIELD_ORDER,
    ...oldParsed.fields.keys(),
    ...newParsed.fields.keys(),
  ]);

  const blocks = ECOMM_AUDIT_FIELD_ORDER.map((label) => {
    if (!keys.has(label)) return null;
    const oldValue = oldParsed.fields.get(label) || "";
    const newValue = newParsed.fields.get(label) || "";
    if (!oldValue && !newValue) return null;
    return {
      label,
      tone: ecommAuditTone(label),
      oldValue,
      newValue,
      changed: hasOld && oldValue !== newValue,
      multiline: label === "Email body" || label === "SMS text",
    };
  }).filter(Boolean);

  return {
    hasOld,
    visitLine: newParsed.visitLine || oldParsed.visitLine,
    templateLine: newParsed.templateLine || oldParsed.templateLine,
    blocks,
  };
}

function looksLikeSetupSummary(text) {
  const value = String(text || "").trim();
  if (!value) return false;
  return /:\s*(On|Off)\b/i.test(value) || /^Group type:/i.test(value);
}

/**
 * Normalize ESch audit rows for the activity log panel.
 */
export function resolveScheduleAuditDisplay(row) {
  const rawAction = String(row?.Action || "").trim();
  const rawNew = String(row?.NewValue || "").trim();
  const rawOld = String(row?.OldValue || "").trim();
  const rawComments = String(row?.Comments || "").trim();
  const fieldName = String(row?.FieldName || "").trim();
  const peeledAction = peelReasonFromAction(rawAction);
  const actionForTitle = peeledAction.title || rawAction;
  const reasonFromAction = peeledAction.reason;

  if (/^Schedule cloned$/i.test(actionForTitle)) {
    const cloneDetail =
      rawNew && !/^Draft$/i.test(rawNew)
        ? rawNew
        : rawComments && !/^Schedule cloned$/i.test(rawComments)
          ? rawComments
          : "";
    return {
      actionDisplay: "Schedule cloned",
      fieldDisplay: "Schedule",
      detailDisplay: cloneDetail,
      reasonDisplay: "",
      showValueDiff: false,
      valueDiffRows: [],
      oldDisplay: "",
      newDisplay: "",
      actorLabel: String(row?.UserName || "").trim() || "—",
      sourceLevel: String(row?.SourceLevel || "").trim(),
    };
  }

  const scheduleRow = isScheduleAuditRow(row);
  const isSetupAudit = isSetupAuditAction(actionForTitle, fieldName);
  const isEcommAudit = isEcommTemplateAuditAction(actionForTitle);

  const { title, suffix } = normalizeActionTitle(actionForTitle);
  const fieldDisplay = isEcommAudit
    ? "E-communication"
    : resolveFieldDisplay(fieldName, actionForTitle);
  const freeTextReason = resolveFreeTextReason({
    rawAction,
    rawComments,
    rawNew,
    reasonFromAction,
  });
  const setupDetail = resolveSetupDetail(actionForTitle, rawComments, rawNew);

  if (isEcommAudit) {
    const hasNew = Boolean(String(rawNew || "").trim());
    const ecommDiff = buildEcommAuditDiffBlocks(rawOld, rawNew);
    const detailParts = [
      ecommDiff.visitLine,
      ecommDiff.templateLine,
    ].filter(Boolean);
    const detail =
      detailParts.length > 0
        ? detailParts.join(" · ")
        : "";
    return {
      actionDisplay: title,
      fieldDisplay,
      detailDisplay: detail,
      reasonDisplay: freeTextReason,
      showEcommView: hasNew && ecommDiff.blocks.length > 0,
      ecommDiff,
      showValueDiff: false,
      valueDiffRows: [],
      oldDisplay: "",
      newDisplay: "",
      actorLabel: String(row?.UserName || "").trim() || "—",
      sourceLevel: String(row?.SourceLevel || "").trim(),
    };
  }

  if (isSetupAudit) {
    const hasOld = Boolean(String(rawOld || "").trim());
    const rawNewIsSetup = looksLikeSetupSummary(rawNew);
    const valueDiffRows = rawNewIsSetup
      ? buildSetupAuditDiffRows(rawOld, rawNew)
      : [];
    const narrowed = rawNewIsSetup
      ? narrowSetupSummaryDiff(rawOld, rawNew)
      : { oldText: String(rawOld || "").trim(), newText: "" };
    const hasNew = Boolean(String(narrowed.newText || "").trim());
    let reason = freeTextReason;
    if (!reason && !rawNewIsSetup && rawNew && rawNew !== rawAction && rawNew !== actionForTitle) {
      reason = rawNew;
    }

    return {
      actionDisplay: title,
      fieldDisplay,
      detailDisplay: hasOld || reason ? "" : setupDetail,
      reasonDisplay: reason || "",
      showValueDiff: hasOld && hasNew,
      valueDiffRows,
      oldDisplay: hasOld ? narrowed.oldText : "",
      newDisplay: hasOld ? narrowed.newText : "",
      actorLabel: String(row?.UserName || "").trim() || "—",
      sourceLevel: String(row?.SourceLevel || "").trim(),
    };
  }

  const detailDisplay = scheduleRow
    ? resolveVisitDetailLine({
        rawAction,
        rawComments,
        rawNew,
        rawOld,
        fieldName,
        suffix,
      })
    : rawComments && rawComments !== rawAction
      ? rawComments
      : "";

  const showValueDiff = isVisitValueDiff(rawOld, rawNew, rawAction, detailDisplay);
  const visitSource =
    rawComments &&
    rawComments !== rawAction &&
    rawComments !== freeTextReason
      ? rawComments
      : rawNew && rawNew !== rawAction && rawNew !== actionForTitle
        ? rawNew
        : suffix || "";
  const visitLine = visitLabelLine(visitSource);

  let oldDisplay = "";
  let newDisplay = "";
  if (showValueDiff) {
    oldDisplay = rawOld;
    newDisplay =
      rawNew && rawNew !== rawAction
        ? rawNew
        : rawComments && rawComments !== rawAction
          ? rawComments
          : detailDisplay && detailDisplay !== rawOld
            ? detailDisplay
            : "";
  }

  return {
    actionDisplay: title,
    fieldDisplay,
    detailDisplay: visitLine || (showValueDiff ? "" : detailDisplay),
    reasonDisplay: freeTextReason,
    showValueDiff,
    valueDiffRows: [],
    oldDisplay,
    newDisplay,
    actorLabel: String(row?.UserName || "").trim() || "—",
    sourceLevel: String(row?.SourceLevel || "").trim(),
  };
}

export function scheduleAuditColumnForSection(section) {
  switch (section) {
    case "setup":
      return "ScheduleSetup";
    case "groups":
    case "groups-visits":
      return "ScheduleGroups";
    case "publish":
      return "ScheduleStatus";
    default:
      return "ScheduleVisits";
  }
}
